import React, { useState, useEffect } from 'react';
import { MdSwapHoriz } from 'react-icons/md';
import Select from 'react-select';
import API from '../api';
import usdtLogo from '../image/usdt-logo.svg';

// Currency options with icons
const currencyOptions = [
  {
    value: 'USD',
    label: (
      <span className="currency-option-label">
        <img src="https://flagcdn.com/us.svg" alt="USD" className="currency-icon" />
        USD
      </span>
    ),
    icon: <img src="https://flagcdn.com/us.svg" alt="USD" className="currency-icon" />,
    text: 'USD',
  },
  {
    value: 'MGA',
    label: (
      <span className="currency-option-label">
        <img src="https://upload.wikimedia.org/wikipedia/commons/b/bc/Flag_of_Madagascar.svg" alt="MGA" className="currency-icon" />
        MGA
      </span>
    ),
    icon: <img src="https://upload.wikimedia.org/wikipedia/commons/b/bc/Flag_of_Madagascar.svg" alt="MGA" className="currency-icon" />,
    text: 'MGA',
  },
  {
    value: 'USDT',
    label: (
      <span className="currency-option-label">
        <img src={usdtLogo} alt="USDT" className="currency-icon" />
        USDT
      </span>
    ),
    icon: <img src={usdtLogo} alt="USDT" className="currency-icon" />,
    text: 'USDT',
  },
];

// Helper to get option by value
const getCurrencyOption = (val) => currencyOptions.find(opt => opt.value === val);

// Helper to get a different currency
const getOtherCurrency = (current, avoid) => {
  return currencyOptions.find(opt => opt.value !== avoid && opt.value !== current)?.value || current;
};

const ExchangePage = ({ user, setUser, formatDate }) => {
  // Currency exchange: default from is MGA and to is USD
  const [exchangeFrom, setExchangeFrom] = useState('MGA');
  const [exchangeTo, setExchangeTo] = useState('USD');
  const [exchangeAmount, setExchangeAmount] = useState('');
  const [exchangeError, setExchangeError] = useState('');
  const [exchangeResult, setExchangeResult] = useState('');
  const [isExchanging, setIsExchanging] = useState(false);

  // Add state for live exchange rate
  const [liveRate, setLiveRate] = useState(null);

  // Add state for real-time insufficient funds error
  const [exchangeAmountError, setExchangeAmountError] = useState('');

  // States for Currency Exchange history overlay
  const [showExchangeHistory, setShowExchangeHistory] = useState(false);
  const [exchangeHistory, setExchangeHistory] = useState(null);

  // Effect to handle exchange history view state from URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    setShowExchangeHistory(urlParams.get('history') === 'true');
    if (showExchangeHistory) {
      fetchExchangeHistory();
    }
  }, [showExchangeHistory]);

  // Listen for browser back/forward so path state stays in sync
  useEffect(() => {
    const handlePopState = () => {
      const urlParams = new URLSearchParams(window.location.search);
      setShowExchangeHistory(urlParams.get('history') === 'true');
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Fetch live rate whenever pair changes
  useEffect(() => {
    if (exchangeFrom && exchangeTo && exchangeFrom !== exchangeTo) {
      setLiveRate(null);
      API.get('/currency-exchange-rate/', { params: { from_currency: exchangeFrom, to_currency: exchangeTo } })
        .then(res => setLiveRate(res.data.rate))
        .catch(() => setLiveRate(null));
    } else {
      setLiveRate(null);
    }
  }, [exchangeFrom, exchangeTo]);

  // Set MGA to USD as default pair when component mounts
  useEffect(() => {
    setExchangeFrom('MGA');
    setExchangeTo('USD');
  }, []);

  // Handler for currency exchange
  const handleExchange = async (e) => {
    e.preventDefault();
    setExchangeError('');
    setExchangeResult('');
    setIsExchanging(true);
    try {
      const res = await API.post('/currency-exchange/', {
        from_currency: exchangeFrom,
        to_currency: exchangeTo,
        amount: exchangeAmount
      });
      const data = res.data;
      setUser(prev => ({
        ...prev,
        usd_balance: data.balance_usd,
        balance_mga: data.balance_mga,
        balance_usdt: data.balance_usdt
      }));
      setExchangeResult(`Converti ${exchangeAmount} ${exchangeFrom} en ${data.converted_amount} ${exchangeTo}`);
      setExchangeAmount('');
    } catch (err) {
      // Translate backend insufficient funds errors to French
      let errorMsg = err.response?.data?.error || "Échec de l'échange";
      if (errorMsg === 'Insufficient MGA balance') errorMsg = 'Fonds MGA insuffisants';
      else if (errorMsg === 'Insufficient USD balance') errorMsg = 'Fonds USD insuffisants';
      else if (errorMsg === 'Insufficient USDT balance') errorMsg = 'Fonds USDT insuffisants';
      setExchangeError(errorMsg);
    } finally {
      setIsExchanging(false);
    }
  };

  // Handler to swap From and To currencies
  const handleSwap = () => {
    const from = exchangeFrom;
    const to = exchangeTo;
    setExchangeFrom(to);
    setExchangeTo(from);
  };

  // Function to update URL when showing/hiding exchange history
  const updateExchangeHistoryState = (show) => {
    const url = new URL(window.location);
    if (show) {
      url.searchParams.set('history', 'true');
    } else {
      url.searchParams.delete('history');
    }
    window.history.pushState({}, '', url);
    setShowExchangeHistory(show);
    if (show) {
      fetchExchangeHistory();
    }
  };

  // Function to fetch exchange history
  const fetchExchangeHistory = async () => {
    try {
      const res = await API.get('/currency-exchanges/');
      setExchangeHistory(res.data);
    } catch (err) {
      console.error('Error fetching exchange history:', err);
      setExchangeHistory([]);
    }
  };

  const balanceForExchange = user ? {
    USD: user.usd_balance,
    MGA: user.balance_mga,
    USDT: user.balance_usdt,
  }[exchangeFrom] : null;

  const parsedBalance = parseFloat(balanceForExchange);
  const availableBalance = Number.isFinite(parsedBalance)
    ? new Intl.NumberFormat('fr-FR', {
        maximumFractionDigits: exchangeFrom === 'MGA' ? 0 : 8,
      }).format(parsedBalance)
    : null;

  return (
    <section className={`exchange-section ${showExchangeHistory ? 'withdraw-section-expanded' : ''}`}>
      <div className={`withdraw-container ${showExchangeHistory ? 'withdraw-container-expanded' : ''}`}>
        <div className="withdraw-header exchange-modern-header">
          <div className="exchange-title-block">
            <div>
              <h2>Échange de devises et cryptos</h2>
              <p>Convertissez vos fonds simplement et en toute transparence.</p>
            </div>
          </div>
          <button
            onClick={() => updateExchangeHistoryState(true)}
            className="exchange-history-button"
            title="Historique des échanges"
            aria-label="Afficher l'historique des échanges"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 8V12L14.5 14.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M5.25 12C5.25 8.27208 8.27208 5.25 12 5.25C15.7279 5.25 18.75 8.27208 18.75 12C18.75 15.7279 15.7279 18.75 12 18.75C10.0716 18.75 8.32466 17.9771 7.06199 16.7145" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <path d="M7 14L5.13397 16.866L2.26795 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
        <div className="withdraw-content">
          {showExchangeHistory ? (
            <div className="withdraw-history">
              <div className="exchange-history-header">
                <h3 className="exchange-history-title">Historique des échanges</h3>
                <button
                  onClick={() => updateExchangeHistoryState(false)}
                  className="exchange-history-close"
                  title="Fermer"
                >
                  ×
                </button>
              </div>
              {exchangeHistory === null ? (
                <div className="exchange-history-loading">
                  Chargement de l'historique...
                </div>
              ) : exchangeHistory.length === 0 ? (
                <div className="exchange-history-empty">
                  Aucun échange enregistré.
                </div>
              ) : (
                <div className="exchange-history-table-wrapper">
                  <table className="exchange-history-table-full">
                    <thead>
                      <tr className="exchange-history-table-header-bg">
                        <th className="exchange-history-table-cell-header-new">Date</th>
                        <th className="exchange-history-table-cell-header-new">Montant</th>
                        <th className="exchange-history-table-cell-header-new">De</th>
                        <th className="exchange-history-table-cell-header-new">Vers</th>
                        <th className="exchange-history-table-cell-header-new">Taux</th>
                        <th className="exchange-history-table-cell-header-new">Montant converti</th>
                      </tr>
                    </thead>
                    <tbody>
                      {exchangeHistory.map((ex) => (
                        <tr key={ex.id} className="exchange-history-table-row-new">
                          <td className="exchange-history-table-data-new" data-label="Date">{formatDate(ex.date)}</td>
                          <td className="exchange-history-table-data-new exchange-history-table-data-bold-new" data-label="Montant">{parseFloat(ex.amount).toFixed(2)} {ex.from_currency}</td>
                          <td className="exchange-history-table-data-new" data-label="De">{ex.from_currency}</td>
                          <td className="exchange-history-table-data-new" data-label="Vers">{ex.to_currency}</td>
                          <td className="exchange-history-table-data-new" data-label="Taux">{parseFloat(ex.rate).toFixed(6)}</td>
                          <td className="exchange-history-table-data-new exchange-history-table-data-bold-new" data-label="Montant converti">{parseFloat(ex.converted_amount).toFixed(2)} {ex.to_currency}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : (
            <div className="exchange-form-container">
              <form className="exchange-form" onSubmit={handleExchange}>
                {/* Exchange rate display */}
                <div className="exchange-rate">
                  <span className="exchange-rate-status">
                    <span className="exchange-live-dot" aria-hidden="true" />
                    Taux en direct
                  </span>
                  {liveRate !== null ? (
                    <strong>1 {exchangeFrom} = {parseFloat(liveRate).toFixed(8)} {exchangeTo}</strong>
                  ) : (
                    <span className="loading-rate">Chargement du taux de change...</span>
                  )}
                </div>
                <div className="exchange-controls-container">
                  <label className="exchange-label exchange-label-flex">
                    <span className="exchange-field-label">Vous envoyez</span>
                    <Select
                      inputId="exchange-from-currency"
                      aria-label="Devise à échanger"
                      className="exchange-currency-select"
                      classNamePrefix="currency-select"
                      options={currencyOptions}
                      value={getCurrencyOption(exchangeFrom)}
                      onChange={opt => {
                        if (opt.value === exchangeTo) {
                          setExchangeFrom(opt.value);
                          setExchangeTo(getOtherCurrency(opt.value, opt.value));
                        } else {
                          setExchangeFrom(opt.value);
                        }
                      }}
                      isSearchable={false}
                    />
                  </label>
                  <button
                    type="button"
                    onClick={handleSwap}
                    className="exchange-swap-button"
                    aria-label="Inverser les devises"
                    title="Inverser les devises"
                  >
                    <MdSwapHoriz size={22} />
                  </button>
                  <label className="exchange-label exchange-label-flex">
                    <span className="exchange-field-label">Vous recevez</span>
                    <Select
                      inputId="exchange-to-currency"
                      aria-label="Devise à recevoir"
                      className="exchange-currency-select"
                      classNamePrefix="currency-select"
                      options={currencyOptions}
                      value={getCurrencyOption(exchangeTo)}
                      onChange={opt => {
                        if (opt.value === exchangeFrom) {
                          setExchangeTo(opt.value);
                          setExchangeFrom(getOtherCurrency(opt.value, opt.value));
                        } else {
                          setExchangeTo(opt.value);
                        }
                      }}
                      isSearchable={false}
                    />
                  </label>
                </div>
                <label className="exchange-label exchange-amount-field">
                  <span className="exchange-field-heading">
                    <span className="exchange-field-label">Montant à échanger</span>
                    {availableBalance !== null && (
                      <span className="exchange-available-balance">
                        Disponible : {availableBalance} {exchangeFrom}
                      </span>
                    )}
                  </span>
                  <div className="amount-input-container">
                    <input
                      type="number"
                      step="any"
                      value={exchangeAmount}
                      onChange={e => {
                        setExchangeAmount(e.target.value);
                        // Real-time insufficient funds check
                        if (!user) {
                          setExchangeAmountError('');
                          return;
                        }
                        const val = parseFloat(e.target.value);
                        let balance = 0;
                        if (exchangeFrom === 'USD') balance = parseFloat(user.usd_balance);
                        else if (exchangeFrom === 'MGA') balance = parseFloat(user.balance_mga);
                        else if (exchangeFrom === 'USDT') balance = parseFloat(user.balance_usdt);
                        if (e.target.value && !isNaN(val) && val > balance) {
                          setExchangeAmountError('Fonds insuffisants');
                        } else {
                          setExchangeAmountError('');
                        }
                      }}
                      required
                      placeholder="0.00"
                      className="exchange-amount-input"
                    />
                    <button
                      type="button"
                      className="max-btn"
                      onClick={() => {
                        if (!user) return;
                        if (exchangeFrom === 'USD') setExchangeAmount(user.usd_balance);
                        else if (exchangeFrom === 'MGA') setExchangeAmount(user.balance_mga);
                        else if (exchangeFrom === 'USDT') setExchangeAmount(user.balance_usdt);
                      }}
                    >
                      Max
                    </button>
                    <span className="exchange-currency-label">{exchangeFrom}</span>
                  </div>
                </label>
                {/* Show real-time insufficient funds error */}
                {exchangeAmountError && (
                  <div className="exchange-amount-error">{exchangeAmountError}</div>
                )}
                {/* Always render the estimated received value container to avoid layout shift */}
                <div className={`exchange-estimate-container ${exchangeAmount && liveRate !== null ? 'has-value' : ''}`}>
                  <span className="exchange-estimate-icon" aria-hidden="true">≈</span>
                  <div className="exchange-estimate-copy">
                    <span className="exchange-estimate-title">Montant estimé</span>
                    {exchangeAmount && liveRate !== null ? (
                      <strong>{ (Math.floor(parseFloat(exchangeAmount) * parseFloat(liveRate) * 1e8) / 1e8).toFixed(8) } {exchangeTo}</strong>
                    ) : liveRate === null && exchangeAmount ? (
                      <span className="exchange-estimate-loading">Calcul du taux...</span>
                    ) : (
                      <span className="exchange-estimate-placeholder">Le résultat apparaîtra ici</span>
                    )}
                  </div>
                </div>
                {exchangeError && <div className="exchange-error-message">{exchangeError}</div>}
                <button type="submit" className="primary-btn" disabled={isExchanging}>
                  <span>{isExchanging ? 'Échange en cours...' : "Confirmer l'échange"}</span>
                  {!isExchanging && (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </button>
                <div className="exchange-security-note">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M12 3l7 3v5c0 4.6-2.9 8.8-7 10-4.1-1.2-7-5.4-7-10V6l7-3z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                    <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Conversion sécurisée avec taux affiché en temps réel
                </div>
              </form>
              {exchangeResult && (
                <div className="exchange-result-container">
                  <div className="exchange-result-success">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="exchange-result-icon">
                      <circle cx="12" cy="12" r="12" fill="#22c55e"/>
                      <path d="M7 13.5l3 3 7-7" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span className="exchange-result-text">{exchangeResult}</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ExchangePage;
